/**
 * The "Your car" band in the header.
 *
 * It reports the stored car and offers a way to change it. Whether the band is
 * on screen at all is the `af-has-car` class on `<html>`, NOT anything this
 * file does on load: `layout/theme.liquid` sets that class from storage in a
 * blocking script before the first paint, so the band is already in the header
 * stack's height when the page is drawn. All this does is keep the class and
 * the label true afterwards, when the car changes on the page.
 *
 * It was an inline `<script>` in the snippet until 2026-08-13. As a file it is
 * cached with the rest of the theme, it is one thing to read rather than markup
 * with logic buried in it, and the page's `Content-Security-Policy` has one
 * fewer inline block to allow.
 */
if (!customElements.get('airflux-car-bar')) {
  customElements.define(
    'airflux-car-bar',
    class AirfluxCarBar extends HTMLElement {
      connectedCallback() {
        this.label = this.querySelector('[data-airflux-carbar-label]');

        this.paint(this.read());
        this.onCarChanged = (e) => this.paint(e.detail);
        document.addEventListener('airflux:car-changed', this.onCarChanged);

        this.querySelector('[data-airflux-carbar-change]')?.addEventListener('click', () =>
          this.change()
        );
      }

      disconnectedCallback() {
        document.removeEventListener('airflux:car-changed', this.onCarChanged);
      }

      /* Through `window.AirfluxStorage`, never `localStorage` directly: the
         car is kept for the visit unconditionally and mirrored across visits
         only on consent. See `snippets/airflux-storage.liquid`. */
      read() {
        return window.AirfluxStorage?.readJSON('airflux:car') || null;
      }

      paint(car) {
        const known = !!car?.label;

        if (known) {
          // The version too, when they picked one — the band is the customer's
          // record of what the site thinks they drive, and "BMW 3 Series" alone
          // is not what they told us.
          this.label.textContent = car.version ? `${car.label} · ${car.version}` : car.label;
        }

        document.documentElement.classList.toggle('af-has-car', known);
      }

      /**
       * Send them to something that can actually change the car. On a page that
       * already carries a finder, open it and scroll to it rather than
       * navigating away from what they were reading.
       */
      change() {
        const widget = document.querySelector('airflux-fitment');
        if (!widget) {
          window.location.href = this.dataset.homeUrl || '/';
          return;
        }
        widget.querySelector('[data-airflux-cascade]')?.removeAttribute('hidden');
        widget.querySelector('[data-airflux-submit]')?.removeAttribute('hidden');
        widget.scrollIntoView({
          block: 'center',
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
            ? 'instant'
            : 'smooth',
        });
      }
    }
  );
}
